import { Sel, STagNew, DataSTag } from '#~/stag/index.js';
import { Icon } from '@studio/design-system/icon';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import type { TriggerProps, Hierarchy } from '../../types.js';
import { Logo } from '../../logo.js';
import { Loading } from '#~/device/components/config/loading.js';

/** Hive/PTZOptics vendor `<Trigger>` component with logo, name, and chevron. */
export const TriggerHive = ({
  className,
  data,
  orgOnly,
  hasRegistryLoaded,
  isRegistryLoading,
  selection,
  selectedOrgId,
  selectedRoomId,
  ...props
}: TriggerProps): ReactElement => {
  // Find the selected studio using selection state first, then fallback to props
  let accountId =
    (data as Hierarchy)?.findIndex((µ) => µ.organization.id === selection?.organizationId) ?? -1;
  let org = (data as Hierarchy)?.[accountId]?.organization;
  let room = (data as Hierarchy)?.[accountId]?.rooms.find((µ) => µ.id === selection?.roomId);

  // Fallback logic when selection is undefined or incomplete
  if (!org && !room) {
    if (orgOnly && selectedOrgId) {
      // For organization-only mode, find by selectedOrgId
      accountId = (data as Hierarchy)?.findIndex((µ) => µ.organization.id === selectedOrgId) ?? -1;
      org = (data as Hierarchy)?.[accountId]?.organization;
    } else if (selectedRoomId) {
      // For room mode, find by selectedRoomId
      for (const item of (data as Hierarchy) ?? []) {
        const foundRoom = item.rooms.find((r) => r.id === selectedRoomId);
        if (foundRoom) {
          org = item.organization;
          room = foundRoom;
          break;
        }
      }
    }
  }

  const displayName = orgOnly ? org?.name : room?.name;
  const logoName = org?.name ?? room?.name ?? '';
  const logoPhoto = org?.photoUrl;

  const hasAnyRooms = ((data as Hierarchy) ?? []).some((µ) => µ.rooms.length > 0);
  const fallbackText = hasRegistryLoaded && !hasAnyRooms ? 'Create a studio' : 'Select a studio';

  const shouldShowLoading = isRegistryLoading && !displayName;
  const finalDisplayName = displayName || (shouldShowLoading ? <Loading /> : fallbackText);

  return (
    <div
      className={twMerge(
        'flex items-center gap-8 bg-base-300 px-12 text-muted transition-colors hover:text-foreground group-x-open:text-foreground',
        className
      )}
      {...STagNew(Sel.SettingsSwitcher, 'trigger', orgOnly ? 'organization' : 'studio')}
      {...props}
    >
      <Logo name={logoName} photo={logoPhoto} />
      <span
        className={`overflow-hidden text-ellipsis whitespace-nowrap ${shouldShowLoading ? 'italic text-muted' : ''}`}
        {...DataSTag(
          Sel.SettingsSwitcher,
          'display-name',
          orgOnly ? selectedOrgId : selectedRoomId
        )}
      >
        {finalDisplayName}
      </span>
      <Icon className='ml-auto transition-transform group-x-open:rotate-180' icon='chevron-up' />
    </div>
  );
};
